/** Pure Announcements list/inspect display mapping — no I/O. D4C. */

import { isEligibleForPublicApi } from "@vital/shared";
import type { StatusTone } from "./overview-ops";
import {
  CONTENT_STATE_LABELS,
  FILTER_ALL,
  FILTER_PAUSED,
  FILTER_QUEUE,
} from "./content-state";

export type AnnouncementInventoryRow = {
  id: string;
  state: string;
  title: string;
  body: string;
  severity: string;
  emergency: boolean;
  emergencyAudience: string | null;
  scope: string;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  submittedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
  rejectionReason: string | null;
  pausedFromState: string | null;
  dismissible: boolean;
};

export type AnnouncementSortKey = "updated" | "title" | "severity";

export const SEVERITY_LABELS_FA: Record<string, string> = {
  info: "اطلاع",
  maintenance: "نگهداری",
  warning: "هشدار",
  success: "موفقیت",
};

export function announcementStateLabelFa(state: string): string {
  return CONTENT_STATE_LABELS[state] ?? state;
}

export function announcementSeverityLabelFa(severity: string): string {
  return SEVERITY_LABELS_FA[severity] ?? severity;
}

export function announcementSeverityTone(severity: string): StatusTone {
  if (severity === "warning") return "warning";
  if (severity === "maintenance") return "warning";
  if (severity === "success") return "success";
  if (severity === "info") return "info";
  return "neutral";
}

export type EligibilityDisplay = {
  label: string;
  tone: StatusTone;
  detail?: string;
};

/**
 * Honest eligibility from lifecycle + existing schedule helper only.
 * Does not invent public-API resolution beyond state/window.
 */
export function announcementEligibility(
  row: Pick<AnnouncementInventoryRow, "state" | "startsAt" | "endsAt">,
  now: Date = new Date(),
): EligibilityDisplay {
  if (row.state === "draft" || row.state === "pending_approval") {
    return { label: "منتشر نشده", tone: "neutral", detail: announcementStateLabelFa(row.state) };
  }
  if (row.state === "paused") {
    return { label: "متوقف — بدون نمایش عمومی", tone: "warning" };
  }
  if (row.state === "archived") {
    return { label: "بایگانی — بدون نمایش عمومی", tone: "neutral" };
  }
  if (row.state === "expired") {
    return { label: "منقضی — بدون نمایش عمومی", tone: "neutral" };
  }
  if (row.state === "published" || row.state === "scheduled") {
    const starts = row.startsAt ? new Date(row.startsAt) : null;
    const ends = row.endsAt ? new Date(row.endsAt) : null;
    const ok = isEligibleForPublicApi(starts, ends, now);
    if (ok) {
      return {
        label: "واجد شرایط نمایش عمومی",
        tone: "success",
        detail: row.state === "scheduled" ? "زمان‌بندی‌شده و داخل بازه" : "منتشرشده و داخل بازه",
      };
    }
    return {
      label: "خارج از پنجرهٔ زمانی",
      tone: "warning",
      detail: announcementStateLabelFa(row.state),
    };
  }
  return { label: "نامشخص", tone: "neutral", detail: row.state };
}

export function matchesAnnouncementQuery(row: AnnouncementInventoryRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    row.title,
    row.body,
    row.severity,
    announcementSeverityLabelFa(row.severity),
    row.state,
    announcementStateLabelFa(row.state),
    row.siteCode ?? "",
    row.siteName ?? "",
    row.groupName ?? "",
    row.scope,
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function matchesAnnouncementListFilter(
  row: AnnouncementInventoryRow,
  listFilter: string,
): boolean {
  if (listFilter === FILTER_ALL) return true;
  return row.state === listFilter;
}

export function filterAndSortAnnouncements(
  rows: AnnouncementInventoryRow[],
  opts: { query: string; listFilter: string; sort: AnnouncementSortKey },
): AnnouncementInventoryRow[] {
  const filtered = rows.filter(
    (row) =>
      matchesAnnouncementListFilter(row, opts.listFilter) &&
      matchesAnnouncementQuery(row, opts.query),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "title") {
      return a.title.localeCompare(b.title, "fa", { sensitivity: "base" });
    }
    if (opts.sort === "severity") {
      return a.severity.localeCompare(b.severity, "en");
    }
    const au = a.updatedAt ?? a.submittedAt ?? a.createdAt ?? "";
    const bu = b.updatedAt ?? b.submittedAt ?? b.createdAt ?? "";
    return bu.localeCompare(au);
  });
  return sorted;
}

export type RelatedAnnouncementLink = { id: string; label: string; to: string };

/** Existing sibling content routes — labels distinguish content families. */
export function relatedAnnouncementLinks(): RelatedAnnouncementLink[] {
  return [
    { id: "sites-preview", label: "پیش‌نمایش مؤثر (سایت‌ها)", to: "/sites#effective-preview" },
    { id: "promotions", label: "پیشنهادها", to: "/promotions" },
    { id: "sponsored", label: "تبلیغات ویژه", to: "/sponsored" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
  ];
}

export { FILTER_ALL, FILTER_QUEUE, FILTER_PAUSED };
